import { Injectable, OnDestroy, Signal, computed, inject, signal } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';

import { Store } from '@ngrx/store';
import {
  Subscription,
  catchError,
  distinctUntilChanged,
  filter,
  forkJoin,
  map,
  of,
  switchMap,
  take,
  iif,
  throwError,
} from 'rxjs';

import { finShare, retryWithBackoff } from '@fcom/rx';
import { Location, LocationSuggestions, LocationGeoLocData } from '@fcom/core-api';
import {
  LatLng,
  LocationRouteCffService,
  equals,
  isPresent,
  unsubscribe,
  isEmptyObjectOrHasEmptyValues,
  StorageService,
} from '@fcom/core';
import { FlightSearchParams, RecommendationService } from '@fcom/common';
import { CommonFeatureState, GlobalBookingActions, GlobalBookingFlight, LocationPair } from '@fcom/common/store';
import {
  selectGlobalBookingLocations,
  selectGlobalBookingFlights,
} from '@fcom/common/store/selectors/global-booking.selectors';
import { LanguageService } from '@fcom/ui-translate';
import { akamaiGeolocation } from '@fcom/core/selectors';

import { createPreviousSearches } from '../utils/utils';
import { PREVIOUS_SEARCHES_KEY } from '../constants';
import { PreviousSearch } from '../interfaces';
import { BookingWidgetAppState } from '../store';
import { BookingWidgetGtmService } from './booking-widget-gtm.service';

@Injectable({ providedIn: 'root' })
export class BookingWidgetFlightService implements OnDestroy {
  private store$ = inject<Store<CommonFeatureState & BookingWidgetAppState>>(Store);
  private languageService = inject(LanguageService);
  private bookingWidgetGtmService = inject(BookingWidgetGtmService);
  private locationRouteCffService = inject(LocationRouteCffService);
  private recommendationService = inject(RecommendationService);
  private storageService = inject(StorageService);

  private readonly _rawBookingLocations = this.store$.selectSignal(selectGlobalBookingLocations);
  readonly bookingLocations: Signal<LocationPair[]> = computed(() => this._rawBookingLocations(), {
    equal: (a, b) => a.length === b.length && b.every((l, i) => equals(l, a[i])),
  });

  readonly locations: Signal<LocationPair> = computed(() => this.bookingLocations()[0], {
    equal: (a, b) => equals(a, b),
  });

  private readonly _rawFlights = this.store$.selectSignal(selectGlobalBookingFlights);
  readonly flights: Signal<GlobalBookingFlight[]> = computed(() => this._rawFlights(), {
    equal: (a, b) => equals(a, b),
  });

  private readonly _suggestedLocationsMap = signal<Record<string, LocationSuggestions>>({});
  private readonly _trendingDestinationsMap = signal<Record<string, Location[]>>({});
  private readonly _defaultLocations = signal<LocationPair[]>([]);

  private readonly _countryCode = toSignal(this.languageService.countryCode, { initialValue: '' });
  readonly suggestedLocations: Signal<LocationSuggestions> = computed(
    () => this._suggestedLocationsMap()[this._countryCode()]
  );

  readonly trendingDestinations: Signal<Location[]> = computed(
    () => this._trendingDestinationsMap()[this.locations()?.origin?.locationCode]
  );

  private subscription = new Subscription();

  constructor() {
    this.getSuggestedLocationBasedOnCountryCode();
    this.getTrendingDestinationsBasedOnOrigin();
    this.initDefaultLocations();
  }

  ngOnDestroy(): void {
    unsubscribe(this.subscription);
  }

  removeFlight(index: number): void {
    this.store$.dispatch(GlobalBookingActions.removeFlight({ index }));
  }

  addFlight(): void {
    this.store$.dispatch(GlobalBookingActions.addFlight());
  }

  setLocations({ origin, destination }: LocationPair, index = 0): void {
    this.bookingWidgetGtmService.trackElementEvent(
      'locations',
      `${origin?.locationCode} - ${destination?.locationCode}`
    );

    this.store$.dispatch(GlobalBookingActions.updateFlight({ flight: { origin, destination }, index }));
  }

  setDefaultLocations(defaultLocations: LocationPair[]): void {
    this._defaultLocations.set(defaultLocations);
  }

  getSuggestedLocationBasedOnCountryCode(): void {
    this.subscription.add(
      this.languageService.countryCode
        .pipe(
          distinctUntilChanged(),
          filter((countryCode) => !this._suggestedLocationsMap()[countryCode]),
          switchMap((countryCode) =>
            this.locationRouteCffService.getSuggestedLocations(countryCode, this.languageService.localeValue).pipe(
              map((suggestedLocationByCountryCode) => ({
                ...this._suggestedLocationsMap(),
                [countryCode]: suggestedLocationByCountryCode,
              }))
            )
          )
        )
        .subscribe((suggestedLocations) => {
          this._suggestedLocationsMap.set(suggestedLocations);
        })
    );
  }

  getTrendingDestinationsBasedOnOrigin(): void {
    this.subscription.add(
      this.store$
        .select(selectGlobalBookingLocations)
        .pipe(
          map((locs) => locs[0]),
          distinctUntilChanged((a, b) => equals(a, b)),
          filter(
            (locations) =>
              !!locations?.origin?.locationCode && !this._trendingDestinationsMap()[locations?.origin?.locationCode]
          ),
          switchMap((locations) =>
            this.recommendationService.getTrendingDestinations(locations?.origin?.locationCode, 0, 10).pipe(
              map((trendingDestinations) => ({
                ...this._trendingDestinationsMap(),
                [locations?.origin?.locationCode]: trendingDestinations,
              }))
            )
          )
        )
        .subscribe((trendingDestinations) => {
          this._trendingDestinationsMap.set(trendingDestinations);
        })
    );
  }

  initDefaultLocations(): void {
    this.subscription.add(
      toObservable(this._defaultLocations)
        .pipe(
          distinctUntilChanged((prev, next) => equals(prev, next)),
          map((defaultLocations) => (isEmptyObjectOrHasEmptyValues(defaultLocations) ? null : defaultLocations)),
          filter(Boolean),
          switchMap((locationsToBeSet) => {
            const locationFromStore = this.locations();
            const origin = isPresent(locationsToBeSet?.[0]?.origin?.locationCode)
              ? locationsToBeSet?.[0]?.origin
              : locationFromStore?.origin;
            const destination = isPresent(locationsToBeSet?.[0]?.destination?.locationCode)
              ? locationsToBeSet?.[0]?.destination
              : locationFromStore?.destination;

            return forkJoin({
              origin: origin?.locationCode
                ? this.locationRouteCffService.bestGuessFor(origin.locationCode, this.languageService.localeValue)
                : of(origin),
              destination: destination?.locationCode
                ? this.locationRouteCffService.bestGuessFor(destination?.locationCode, this.languageService.localeValue)
                : of(destination),
            });
          }),
          finShare()
        )
        .subscribe((location) => {
          this.setLocations(location);
        })
    );
  }

  // fetch user geoLocation data and set to origin, when origin data is missing or default location is missing
  fetchUserLocationAndSetDefaultOrigin(): void {
    const currentLocation = this.locations();
    const currentDefaultLocations = this._defaultLocations();
    const shouldProceed =
      !isPresent(currentLocation?.origin) ||
      (currentDefaultLocations.length > 0 && !isPresent(currentDefaultLocations[0]?.origin));

    if (!shouldProceed) {
      return;
    }

    this.subscription.add(
      this.store$
        .pipe(akamaiGeolocation())
        .pipe(
          take(1),
          switchMap((value: LatLng) =>
            this.locationRouteCffService.geolocMatchFor(value.lat, value.lng, this.languageService.localeValue)
          ),
          switchMap((location: LocationGeoLocData) =>
            this.locationRouteCffService.bestGuessFor(location.item.locationCode, this.languageService.localeValue)
          ),
          retryWithBackoff(2),
          catchError((err: unknown) => {
            return throwError(() => err);
          }),
          switchMap((cffLocation: Location) => {
            const defaultLocations = this._defaultLocations();
            return iif(
              () => isPresent(defaultLocations?.[0]?.destination?.locationCode),
              this.locationRouteCffService
                .bestGuessFor(defaultLocations?.[0]?.destination?.locationCode, this.languageService.localeValue)
                .pipe(map((destination) => [cffLocation, destination])),
              of([cffLocation])
            );
          }),
          finShare()
        )
        .subscribe(([cffLocation, destination]) => {
          this.setLocations({
            origin: cffLocation,
            ...(isPresent(destination) && { destination }),
          });
        })
    );
  }

  setPreviousSearchToLocalStorage = (flightSearchParams: FlightSearchParams): void => {
    if (flightSearchParams.isAward) {
      return;
    }

    let previousSearches: PreviousSearch[];
    const previousSearchesStorage = this.storageService.LOCAL.get(PREVIOUS_SEARCHES_KEY);

    try {
      previousSearches = JSON.parse(previousSearchesStorage) ?? [];
    } catch {
      previousSearches = [];
    }

    const updatedPreviousSearches = createPreviousSearches(flightSearchParams, previousSearches);
    this.storageService.LOCAL.set(PREVIOUS_SEARCHES_KEY, JSON.stringify(updatedPreviousSearches));
  };

  setPreviousFlightSelection(previousSearch: PreviousSearch): void {
    this.store$.dispatch(GlobalBookingActions.setSelection({ selection: previousSearch }));
  }
}
